import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api/client';
import { toast } from '@/hooks/use-toast';
import { getApiMessage, getApiSuccess } from '@/lib/apiMessage';
import {
  CheckCircle2,
  Building2,
  ShieldCheck,
  Headphones,
  ArrowUpRight,
  Rocket,
  CalendarClock,
  Target,
  Settings,
  BarChart3,
} from 'lucide-react';

// NOTE: This page intentionally mirrors patterns used in `WorkspaceBilling.tsx` for visual consistency.
// Data will later be hydrated from backend (workspace subscription, usage metrics, invoices, upgrade status).

// NOTE: The rest of the page (plan/usage/invoices) is intentionally commented out for now
// to focus only on the Enterprise Upgrade card shown in the attached screenshot.

const enterpriseFeatures = [
  {
    title: 'Seamless Onboarding',
    description: 'Guided setup, best-practice defaults with templates, and zero-downtime activation for fast go-live.',
    icon: Rocket,
  },
  {
    title: 'Scheduled Collection from Portals',
    description: 'Automate periodic portal retrievals on your preferred cadence with robust retry handling.',
    icon: CalendarClock,
  },
  {
    title: 'Parse Data with 100% Accuracy',
    description: 'Human-in-the-loop verification and deterministic rules to ensure pixel-perfect extraction.',
    icon: Target,
  },
  {
    title: 'Validations',
    description: 'Multi-layered data validation with cross-field consistency checks to guarantee billing accuracy and compliance.',
    icon: Settings,
  },
  {
    title: 'Analytics',
    description: 'AI-driven consumption analytics and anomaly detection to uncover trends, inefficiencies, and cost-saving opportunities.',
    icon: BarChart3,
  },
  {
    title: 'Dedicated Account Manager',
    description: 'Single point of contact for personalized assistance and faster resolutions.',
    icon: Building2,
  },
  {
    title: 'Advanced Security & Compliance',
    description: 'Enterprise-grade protection, audit logs, ISO & SOC 2 alignment.',
    icon: ShieldCheck,
  },
  {
    title: 'Priority & 24/7 Support',
    description: 'Guaranteed response times with round-the-clock expert availability.',
    icon: Headphones,
  },
  {
    title: 'Custom Integrations',
    description: 'Seamless ERP / CRM integration & bespoke API workflows.',
    icon: CheckCircle2,
  },
];

export default function ManageSubscription() {
  const [upgradeRequested, setUpgradeRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // const [loading, setLoading] = useState(true);
  // const [data, setData] = useState<SubscriptionData | null>(null);
  // useEffect(() => {
  //   // Simulate async fetch (commented out for now)
  //   const timer = setTimeout(() => {
  //     setData(null);
  //     setLoading(false);
  //   }, 400);
  //   return () => clearTimeout(timer);
  // }, []);

  const requestEnterpriseUpgrade = async () => {
    if (submitting) return;
    // Read workspaceId from session storage as requested
    let workspaceId: string | null = null;
    try { workspaceId = sessionStorage.getItem('currentWorkspaceId'); } catch (_) {}
    if (!workspaceId) {
      toast({ title: 'Workspace not selected', description: 'Please select a workspace first.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/v1/subscription/request-subscription', null, { params: { workspaceId } });
      const success = getApiSuccess(res);
      const message = getApiMessage(res) || (success ? 'Enterprise access request submitted.' : 'Request could not be processed.');
      if (success === false) {
        toast({ title: 'Request Failed', description: message, variant: 'destructive' });
        return;
      }
      toast({ title: 'Request Submitted', description: message });
      setUpgradeRequested(true);
    } catch (err: any) {
      const message = getApiMessage(err) || err?.response?.data?.message || 'Failed to submit request.';
      toast({ title: 'Request Failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container max-w-3xl mx-auto p-6 space-y-8">
      {/* Enterprise Upgrade / CTA ONLY (everything else is commented out below) */}
      <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              Enterprise Upgrade
              {!upgradeRequested && (
                <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200">Available</Badge>
              )}
            </CardTitle>
            <CardDescription>Unlock advanced features & support</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!upgradeRequested ? (
              <>
                <ul className="space-y-2 text-sm">
                  {enterpriseFeatures.map((f) => (
                    <li key={f.title} className="flex items-start gap-2">
                      <f.icon className="h-4 w-4 text-indigo-600 mt-0.5" />
                      <div>
                        <span className="font-medium">{f.title}</span>
                        <p className="text-muted-foreground text-xs leading-relaxed">{f.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <Button className="w-full" onClick={requestEnterpriseUpgrade} disabled={submitting}>
                  Request Enterprise Access <ArrowUpRight className="h-4 w-4 ml-2" />
                </Button>
              </>
            ) : (
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Request received</p>
                  <p className="text-muted-foreground text-xs">Our team will reach out shortly to finalize your Enterprise upgrade.</p>
                </div>
              </div>
            )}
          </CardContent>
      </Card>

      {/*
        ===================== COMMENTED OUT FOR LATER =====================
        Header, Plan & Usage, and Invoices sections have been commented out
        to align with the request to only show the Enterprise Upgrade card.

        Original sections preserved below for future development.

        <div className="flex items-center justify-between flex-wrap gap-4">...</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">...</div>
        <Card>... Recent Invoices ...</Card>
      */}
    </div>
  );
}